import { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import useWorkspaceNavigate from '../../hooks/useWorkspaceNavigate';
import { createApplicantNavigation } from '../../lib/applicantNavigation';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { 
  Search, Users, Filter, Loader2, ChevronRight, CheckCircle, Clock, 
  AlertTriangle, User, XCircle, Shield, FileText, Briefcase,
  MoreHorizontal, Eye, UserCheck, Inbox, UserPlus, Send, Copy, MessageCircle
} from 'lucide-react';
import WorkspaceLink from '../../components/workspace/WorkspaceLink';
import PeopleWorkspaceShell from '../../components/people/PeopleWorkspaceShell';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { LabeledProgressBadge } from '../../components/compliance/LabeledProgressMetrics';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';
import { Info } from 'lucide-react';
import EmployeeAvatar from '../../components/portal/EmployeeAvatar';
import ProfileMessageDialog from '../../components/portal/ProfileMessageDialog';
import { cn } from '../../lib/utils';
import { isApplicantStatus, normalizeLifecycleStatus } from '../../lib/lifecycle';
import TenantSetupGateNotice from '../../components/compliance/TenantSetupGateNotice';
import { extractTenantSetupGate, tenantSetupGateToastMessage } from '../../utils/tenantSetupGate';
import { createMessageRequestId } from '../../lib/manualMessaging';
import { plainRecruitmentNextStep } from '../../lib/plainRecruitmentActions';

// Canonical statuses for which to fetch per-card status
const CANONICAL_STATUSES = [
  'new',
  'screening',
  'interview',
  'compliance_review',
];
import API_BASE from '../../utils/apiBase';
import './recruitmentSurface.css';
import ActionableTaskQueue from '../../components/admin/ActionableTaskQueue';

const API = API_BASE;

// One cancellable, bounded queue per applicant-list generation. Each ID is
// scheduled at most once; stale results are ignored after a filter/tenant change.
export const runBoundedRecruitmentApprovalChecks = (applicants, fetchOne, onResult, onError, limit = 3) => {
  const controller = new AbortController();
  const seen = new Set();
  const queue = (applicants || []).filter((applicant) => {
    if (!applicant?.id || seen.has(applicant.id)) return false;
    seen.add(applicant.id);
    return true;
  });
  let cursor = 0;
  let inFlight = 0;
  const pump = () => {
    while (!controller.signal.aborted && inFlight < limit && cursor < queue.length) {
      const applicant = queue[cursor++];
      inFlight += 1;
      Promise.resolve()
        .then(() => controller.signal.aborted ? undefined : fetchOne(applicant, controller.signal))
        .then((result) => {
          if (!controller.signal.aborted) onResult(applicant, result);
        })
        .catch((error) => {
          if (!controller.signal.aborted) onError(applicant, error);
        })
        .finally(() => {
          inFlight -= 1;
          pump();
        });
    }
  };
  pump();
  return () => controller.abort();
};

// Recruitment stage labels
const STAGE_LABELS = {
  new: 'New',
  screening: 'Screening',
  interview: 'Interview',
  compliance_review: 'Compliance Review',
};

// Stage colors
const STAGE_COLORS = {
  new: 'bg-blue-100 text-blue-800 border-blue-200',
  screening: 'bg-purple-100 text-purple-800 border-purple-200',
  interview: 'bg-amber-100 text-amber-800 border-amber-200',
  compliance_review: 'bg-orange-100 text-orange-800 border-orange-200',
};

const formatDateTime = (value) => {
  if (!value) return 'Not recorded';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value).slice(0, 10);
  return parsed.toLocaleString(undefined, { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const timeInStage = (value) => {
  if (!value) return 'Unknown';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'Unknown';
  const days = Math.max(0, Math.floor((Date.now() - parsed.getTime()) / 86400000));
  if (days === 0) return 'Today';
  if (days === 1) return '1 day';
  return `${days} days`;
};

export const isApplicantActionableJourneyAction = (action) => {
  if (!action) return false;
  if (action.awaiting_final_recruitment_approval === true) return false;
  const type = String(action.type || '').trim().toLowerCase();
  return ![
    'awaiting_admin_review',
    'awaiting_review',
    'waiting_for_admin',
    'no_action',
  ].includes(type);
};

export const recruitmentEmploymentReviewAction = (approval) => {
  const blockers = Array.isArray(approval?.recruitmentBlockers)
    ? approval.recruitmentBlockers
    : [];
  const blocker = blockers.find(
    (item) => item?.requirement_key === 'employment_review_sign_off',
  );
  if (!blocker) return null;

  const owner = String(blocker.owner || 'admin').toLowerCase() === 'applicant'
    ? 'Applicant'
    : 'Osabea';
  return {
    owner,
    label: blocker.next_action_label || blocker.reason || 'Complete Employment Review',
    action: blocker.next_action || 'open_employment_review',
  };
};

export const matchesRecruitmentFocusFilter = (applicant, approval, focus = '') => {
  if (!focus || focus === 'all') return true;
  const stage = normalizeLifecycleStatus(applicant?.status);
  const nextWorkerAction = applicant?.journey?.next_worker_action || null;
  const nextAdminAction = applicant?.journey?.next_admin_action || null;
  const applicantActionable = isApplicantActionableJourneyAction(nextWorkerAction);
  const passiveWorkerWait = Boolean(nextWorkerAction) && !applicantActionable;
  const employmentReviewAction = recruitmentEmploymentReviewAction(approval);

  if (focus === 'access_support') {
    return !applicant?.portal_last_login_at;
  }
  if (focus === 'applicant_action') {
    return employmentReviewAction
      ? employmentReviewAction.owner === 'Applicant'
      : applicantActionable;
  }
  // Keep this legacy filter key stable for saved URLs and existing workflow tests.
  // Display copy is tenant-neutral: every care provider has its own team.
  if (focus === 'osabea_action') {
    if (employmentReviewAction) return employmentReviewAction.owner === 'Osabea';
    // The primary owner must be unique; applicant-owned actions do not also
    // appear in the team's list unless a separate explicit manager review exists.
    if (applicantActionable) return false;
    return passiveWorkerWait
      || Boolean(nextAdminAction)
      || ['new', 'screening', 'interview'].includes(stage)
      || (stage === 'compliance_review' && !approval?.canApprove);
  }
  if (focus === 'ready_for_approval') {
    return stage === 'compliance_review' && approval?.canApprove === true && approval?.statusUnavailable !== true;
  }
  return true;
};

export const recruitmentPortalAccessState = (applicant) => {
  if (applicant?.portal_last_login_at) {
    return {
      label: `Portal accessed ${formatDateTime(applicant.portal_last_login_at)}`,
      tone: 'green',
      state: 'accessed',
    };
  }

  const issuedAt = applicant?.portal_last_link_created_at
    || applicant?.private_invite_last_link_created_at
    || applicant?.private_invite_last_sent_at;

  if (issuedAt) {
    return {
      label: `Access link issued ${formatDateTime(issuedAt)} · not opened yet`,
      tone: 'amber',
      state: 'issued_not_opened',
    };
  }

  return {
    label: 'No portal access link issued yet',
    tone: 'slate',
    state: 'not_issued',
  };
};

export const recruitmentAccessRescue = (applicant, nextAction) => {
  if (nextAction?.owner !== 'Applicant') return null;

  const access = recruitmentPortalAccessState(applicant);
  if (access.state === 'accessed') return null;

  if (access.state === 'not_issued') {
    return {
      state: 'not_issued',
      title: 'Applicant cannot complete this yet',
      description: 'Copy the secure application link and share it through WhatsApp or another channel.',
      copyLabel: 'Copy secure link',
    };
  }

  return {
    state: 'issued_not_opened',
    title: 'Applicant has not opened their application link',
    description: 'Copy a fresh secure link if they need help getting back in.',
    copyLabel: 'Copy fresh link',
  };
};

export const recruitmentFocusBadge = (applicant, approval) => {
  // An unavailable current check cannot inherit a stale green readiness badge.
  if (approval?.statusUnavailable === true) return { label: 'Check status', tone: 'slate' };
  const stage = normalizeLifecycleStatus(applicant?.status);
  const nextWorkerAction = applicant?.journey?.next_worker_action || null;
  const applicantActionable = isApplicantActionableJourneyAction(nextWorkerAction);
  const employmentReviewAction = recruitmentEmploymentReviewAction(approval);
  if (employmentReviewAction) {
    return employmentReviewAction.owner === 'Applicant'
      ? { label: 'Waiting on applicant', tone: 'amber' }
      : { label: 'Team action', tone: 'blue' };
  }
  if (stage === 'compliance_review' && approval?.canApprove) {
    return { label: 'Ready for approval', tone: 'green' };
  }
  if (applicantActionable) {
    return { label: 'Waiting on applicant', tone: 'amber' };
  }
  if (
    applicant?.journey?.next_admin_action
    || ['new', 'screening', 'interview'].includes(stage)
    || (stage === 'compliance_review' && approval && !approval?.canApprove)
  ) {
    return { label: 'Team action', tone: 'blue' };
  }
  return null;
};

export const recruitmentNextActionSummary = (applicant, approval) => {
  const workerAction = applicant?.journey?.next_worker_action || null;
  const adminAction = applicant?.journey?.next_admin_action || null;
  const applicantActionable = isApplicantActionableJourneyAction(workerAction);
  const stage = normalizeLifecycleStatus(applicant?.status);
  const employmentReviewAction = recruitmentEmploymentReviewAction(approval);
  if (employmentReviewAction) {
    return {
      owner: employmentReviewAction.owner,
      label: employmentReviewAction.label,
    };
  }

  if (applicantActionable) {
    return {
      owner: 'Applicant',
      label: workerAction.title || workerAction.label || workerAction.action || 'Complete the next onboarding step',
    };
  }

  if (adminAction) {
    return {
      owner: 'Osabea',
      label: adminAction.label || adminAction.title || adminAction.action || 'Complete the next recruitment review step',
    };
  }

  if (stage === 'compliance_review' && approval?.canApprove && approval?.statusUnavailable !== true) {
    return { owner: 'Osabea', label: 'Approve recruitment to move this person into onboarding' };
  }

  if (workerAction && !applicantActionable) {
    return {
      owner: 'Osabea',
      label: adminAction?.label
        || adminAction?.title
        || 'Complete the care provider review',
    };
  }

  return {
    owner: 'Osabea',
    label: applicant?.next_required_action || recruitmentStageActionLabel(applicant, approval),
  };
};

export const recruitmentStageActionLabel = (applicant, approval) => {
  const employmentReviewAction = recruitmentEmploymentReviewAction(approval);
  if (employmentReviewAction) {
    return employmentReviewAction.label;
  }
  if (applicant?.journey?.next_admin_action?.label) {
    return applicant.journey.next_admin_action.label;
  }
  const stage = normalizeLifecycleStatus(applicant?.status);
  if (stage === 'new') return 'Start Screening';
  if (stage === 'screening') return 'Schedule Interview';
  if (stage === 'interview') return 'Open Interview';
  if (stage === 'compliance_review' && approval?.canApprove && approval?.statusUnavailable !== true) return 'Approve to Onboarding';
  if (stage === 'compliance_review') return 'Complete Recruitment Review';
  return applicant?.next_required_action || 'Review Applicant';
};

export const recruitmentStageActionTarget = (applicant, approval) => {
  const stage = normalizeLifecycleStatus(applicant?.status);
  const actionType = applicant?.journey?.next_admin_action?.type;
  const employmentReviewAction = recruitmentEmploymentReviewAction(approval);
  if (employmentReviewAction) {
    return { type: 'navigate', to: `/portal/recruitment/${applicant.id}?tab=employment` };
  }
  if (stage === 'new' || actionType === 'start_screening') {
    return { type: 'start_screening' };
  }
  if (actionType === 'schedule_interview') {
    return { type: 'navigate', to: `/portal/recruitment/${applicant.id}?tab=forms&action=schedule_interview` };
  }
  if (actionType === 'open_interview' || actionType === 'record_interview_decision' || stage === 'interview') {
    return { type: 'navigate', to: `/portal/recruitment/${applicant.id}?tab=forms` };
  }
  if (stage === 'compliance_review' && approval?.canApprove && approval?.statusUnavailable !== true) {
    return { type: 'approve' };
  }
  return { type: 'navigate', to: `/portal/recruitment/${applicant.id}?tab=checklist` };
};

export default function RecruitmentPage() {
  const navigate = useWorkspaceNavigate();
  const location = useLocation();
  const returnFilters = location.state?.recruitmentFilters || {};
  const headerQuery = new URLSearchParams(location.search).get('q');
  const { token, user } = useAuth();
  
  const [pipeline, setPipeline] = useState(null);
  const [applicants, setApplicants] = useState([]);
  const [approvalData, setApprovalData] = useState({}); // Cache approval checks
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(() => headerQuery !== null
    ? headerQuery : (typeof returnFilters.search === 'string' ? returnFilters.search : ''));
  useEffect(() => {
    // A query-only navigation from the global header must update this live
    // recruitment search, even if React keeps this route mounted.
    const q = new URLSearchParams(location.search).get('q');
    if (q !== null) setSearch(q);
  }, [location.search]);
  const [statusFilter, setStatusFilter] = useState(() => typeof returnFilters.statusFilter === 'string' ? returnFilters.statusFilter : '');
  const [focusFilter, setFocusFilter] = useState(() => typeof returnFilters.focusFilter === 'string' ? returnFilters.focusFilter : '');
  
  // Approval dialog state
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [blockersDialogOpen, setBlockersDialogOpen] = useState(false);
  const [selectedApplicant, setSelectedApplicant] = useState(null);
  const [approvalNotes, setApprovalNotes] = useState('');
  const [isApproving, setIsApproving] = useState(false);
  const [loadingApprovalCheck, setLoadingApprovalCheck] = useState(null);
  const [statusChecksUnavailable, setStatusChecksUnavailable] = useState(false);
  const [approvalSetupGate, setApprovalSetupGate] = useState(null);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    role: 'Healthcare Assistant',
    notes: ''
  });
  const [inviteResult, setInviteResult] = useState(null);
  const [inviteSetupGate, setInviteSetupGate] = useState(null);
  const [isInviting, setIsInviting] = useState(false);
  const [portalAccessLoading, setPortalAccessLoading] = useState(null);
  const [messageApplicant, setMessageApplicant] = useState(null);
  const [integrityAuditOpen, setIntegrityAuditOpen] = useState(false);
  const [integrityAuditLoading, setIntegrityAuditLoading] = useState(false);
  const [integrityAudit, setIntegrityAudit] = useState(null);
  const [contactConflictReviews, setContactConflictReviews] = useState({});
  const [contactConflictLoading, setContactConflictLoading] = useState(null);
  const [contactConflictResolveLoading, setContactConflictResolveLoading] = useState(null);
  const [contactConflictConfirmed, setContactConflictConfirmed] = useState({});
  const [integritySourceConsistency, setIntegritySourceConsistency] = useState(null);
  const [legacyCodeCleanupLoading, setLegacyCodeCleanupLoading] = useState(false);

  const canApprove = user?.role === 'super_admin' || user?.role === 'admin';
  const canInviteApplicants = ['super_admin', 'admin', 'branch_manager'].includes(user?.role);
  const canRunIntegrityAudit = ['super_admin', 'admin'].includes(user?.role);

  const runPeopleIntegrityAudit = async () => {
    if (!canRunIntegrityAudit || integrityAuditLoading) return;
    setIntegrityAuditLoading(true);
    try {
      const [auditResponse, sourceResponse] = await Promise.all([
        axios.get(`${API}/admin/people-integrity-audit`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API}/admin/people-integrity-source-consistency`, {
          headers: { Authorization: `Bearer ${token}` },
        }).catch(() => ({ data: null })),
      ]);
      setIntegrityAudit(auditResponse.data || null);
      setIntegritySourceConsistency(sourceResponse.data || null);
      setIntegrityAuditOpen(true);
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not run the people integrity audit');
    } finally {
      setIntegrityAuditLoading(false);
    }
  };

  const applyControlledLegacyCodeCleanup = async () => {
    if (!canRunIntegrityAudit || legacyCodeCleanupLoading || !integrityAudit?.workspace_id) return;
    const safeRows = (integrityAudit.people || []).filter(
      (row) => row?.legacy_employee_code_review?.classification === 'safe_legacy_residue_candidate'
    );
    const anomalyCount = Number(integrityAudit?.anomaly_counts?.employee_code_present_before_activation || 0);
    if (!integritySourceConsistency?.sources_consistent || anomalyCount !== 4 || safeRows.length !== 4) {
      toast.error('CareOps refused the cleanup because the reviewed four-record state has changed.');
      return;
    }

    setLegacyCodeCleanupLoading(true);
    try {
      const response = await axios.post(
        `${API}/admin/people-integrity/legacy-code-cleanup`,
        {
          apply: true,
          confirm_workspace_id: integrityAudit.workspace_id,
          confirm_target_count: 4,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!response.data?.verified || Number(response.data?.applied_count || 0) !== 4) {
        throw new Error('Cleanup response was not fully verified');
      }
      toast.success('Archived and cleared 4 safe legacy employee codes.');
      await runPeopleIntegrityAudit();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      const message = typeof detail === 'string'
        ? detail
        : detail?.message || 'Controlled legacy-code cleanup was refused';
      toast.error(message);
    } finally {
      setLegacyCodeCleanupLoading(false);
    }
  };

  const resolvePhoneConflict = async (personId, resolution) => {
    if (!contactConflictConfirmed[personId] || contactConflictResolveLoading) return;
    setContactConflictResolveLoading(`${personId}:${resolution}`);
    try {
      const response = await axios.post(
        `${API}/admin/people/${personId}/contact-conflict-resolution`,
        { resolution, independently_confirmed: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!response.data?.resolved) throw new Error('Contact resolution was not confirmed by CareOps');
      toast.success(
        resolution === 'profile_confirmed_current'
          ? 'Confirmed the profile number as the current contact.'
          : 'Updated the current profile from the submitted application number.'
      );
      setContactConflictReviews((prev) => {
        const next = { ...prev };
        delete next[personId];
        return next;
      });
      setContactConflictConfirmed((prev) => ({ ...prev, [personId]: false }));
      await runPeopleIntegrityAudit();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : detail?.message || 'Could not resolve phone conflict');
    } finally {
      setContactConflictResolveLoading(null);
    }
  };

  const reviewPhoneConflict = async (personId) => {
    if (!canRunIntegrityAudit || !personId || contactConflictLoading) return;
    setContactConflictLoading(personId);
    try {
      const response = await axios.get(`${API}/admin/people/${personId}/contact-conflict-review`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setContactConflictReviews((prev) => ({
        ...prev,
        [personId]: response.data || null,
      }));
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not review the phone conflict');
    } finally {
      setContactConflictLoading(null);
    }
  };

  // Fetch pipeline summary (this page has its own dedicated "recruitment dashboard" metrics)
  const fetchPipeline = async () => {
    try {
      const response = await axios.get(`${API}/recruitment/pipeline`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPipeline(response.data);
    } catch (error) {
      console.error('Failed to fetch pipeline:', error);
    }
  };

  // Fetch applicants list
  const fetchApplicants = async (signal) => {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (statusFilter) params.append('status', statusFilter);

      const response = await axios.get(`${API}/recruitment/applicants?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
        ...(signal ? { signal } : {}),
      });
      if (signal?.aborted) return;
      const applicantRows = (response.data || []).filter((person) => (
        isApplicantStatus(normalizeLifecycleStatus(person?.status))
      ));
      setApplicants(applicantRows);
      // Per-card approval checks are deferred to the bounded queue below.
    } catch (error) {
      if (signal?.aborted) return;
      console.error('Failed to fetch applicants:', error);
      setStatusChecksUnavailable(true);
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  };

  // One canonical list-card check: Recruitment Approval. Employee Staff File,
  // Work Readiness and promotion belong to People → Onboarding after handoff.
  // The caller owns the result; an aborted generation cannot write stale status.
  const fetchApprovalStatus = async (applicant, signal) => {
    const approvalRes = await axios.get(
      `${API}/employees/${applicant.id}/recruitment-approval-check`,
      { headers: { Authorization: `Bearer ${token}` }, signal }
    );
    const recruitmentProgress = approvalRes.data?.recruitment_progress || {};
    const recruitmentProgressPct = recruitmentProgress.percentage == null
      ? null
      : Number(recruitmentProgress.percentage);
    return {
      ...approvalRes.data,
      progressPct: recruitmentProgressPct,
      recruitmentProgress,
      status_unavailable: Boolean(approvalRes.data?.status_unavailable),
    };
  };

  useEffect(() => {
    const controller = new AbortController();
    // Prevent stale previous-filter or previous-tenant approval cards.
    setApplicants([]);
    setApprovalData({});
    setStatusChecksUnavailable(false);
    setLoading(true);
    fetchPipeline();
    fetchApplicants(controller.signal);
    return () => controller.abort();
  }, [token, search, statusFilter]);

  // Approval data updates must NOT restart this queue. Abort outstanding
  // requests on applicant-list or tenant change, with a global limit of three.
  useEffect(() => {
    const toFetch = applicants.filter(
      (a) => CANONICAL_STATUSES.includes(normalizeLifecycleStatus(a.status)) && !approvalData[a.id]
    );
    return runBoundedRecruitmentApprovalChecks(
      toFetch,
      fetchApprovalStatus,
      (applicant, result) => {
        setApprovalData((prev) => ({ ...prev, [applicant.id]: result }));
        if (result?.status_unavailable) setStatusChecksUnavailable(true);
      },
      (applicant) => {
        setStatusChecksUnavailable(true);
        setApprovalData((prev) => ({
          ...prev,
          [applicant.id]: {
            can_approve: false,
            blocker_count: 0,
            verified_count: 0,
            required_count: 0,
            progressPct: null,
            status_unavailable: true,
            message: 'Status unavailable',
          },
        }));
      },
    );
  }, [applicants, token]);

  // Applicant cards use recruitment-gate truth only.
  const getApprovalSummary = (applicantId) => {
    const data = approvalData[applicantId];
    if (!data) return null;
    return {
      canApprove: data.can_approve,
      blockerCount: data.blocker_count || 0,
      verifiedCount: data.verified_count || 0,
      requiredCount: data.required_count || 0,
      statusUnavailable: Boolean(data.status_unavailable),
      recruitmentBlockers: Array.isArray(data.blockers) ? data.blockers : [],
    };
  };

  const visibleApplicants = useMemo(
    () => applicants.filter((applicant) => (
      matchesRecruitmentFocusFilter(applicant, getApprovalSummary(applicant.id), focusFilter)
    )),
    [applicants, approvalData, focusFilter],
  );

  // Navigate within the visible, filtered tenant-scoped list (no new list read).
  const applicantNavigation = (applicantId) => createApplicantNavigation(
    visibleApplicants, applicantId, { search, statusFilter, focusFilter }
  );
  const handleReviewApplicant = (applicantId) => {
    navigate(`/portal/recruitment/${applicantId}`, {
      state: { applicantNavigation: applicantNavigation(applicantId) },
    });
  };

  const handleStartScreening = async (applicant) => {
    try {
      await axios.post(
        `${API}/recruitment/applicants/${applicant.id}/start-screening`,
        { reason: 'Screening started from recruitment card' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Applicant moved to Screening');
      fetchPipeline();
      fetchApplicants();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : detail?.message || 'Could not start screening');
    }
  };

  const handleStageAction = (applicant, approval) => {
    const target = recruitmentStageActionTarget(applicant, approval);
    if (target.type === 'start_screening') {
      handleStartScreening(applicant);
      return;
    }
    if (target.type === 'navigate') {
      navigate(target.to, {
        state: { applicantNavigation: applicantNavigation(applicant.id) },
      });
      return;
    }
    if (target.type === 'approve') {
      handleApprovalClick(applicant);
    }
  };

  const stageActionLabel = (applicant, approval) => {
    return recruitmentStageActionLabel(applicant, approval);
  };

  const resetInviteForm = () => {
    setInviteForm({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      role: 'Healthcare Assistant',
      notes: ''
    });
    setInviteResult(null);
    setInviteSetupGate(null);
  };

  const handleCreateInvite = async () => {
    setIsInviting(true);
    setInviteSetupGate(null);
    setInviteResult(null);
    try {
      const response = await axios.post(
        `${API}/recruitment/private-applicant-invites`,
        { ...inviteForm, send_email: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setInviteResult(response.data);
      toast.success(response.data?.invite_sent ? 'Private applicant invite sent' : 'Private applicant link created');
      fetchPipeline();
      fetchApplicants();
    } catch (error) {
      const gate = extractTenantSetupGate(error);
      if (gate) {
        setInviteSetupGate(gate);
        toast.error(tenantSetupGateToastMessage(gate));
        return;
      }
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : detail?.message || 'Failed to create applicant invite');
    } finally {
      setIsInviting(false);
    }
  };

  const copyInviteLink = async () => {
    if (!inviteResult?.portal_url) return;
    try {
      await navigator.clipboard.writeText(inviteResult.portal_url);
      toast.success('Private invite link copied');
    } catch {
      toast.error('Could not copy link');
    }
  };

  const handleApplicantPortalAccess = async (applicant, { sendEmail }) => {
    const actionKey = `${applicant.id}:${sendEmail ? 'send' : 'copy'}`;
    setPortalAccessLoading(actionKey);
    try {
      const response = await axios.post(
        `${API}/recruitment/applicants/${applicant.id}/portal-access`,
        {
          send_email: sendEmail,
          reason: sendEmail ? 'Admin sent applicant portal access from Recruitment' : 'Admin copied applicant portal link from Recruitment',
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (sendEmail) {
        toast.success(response.data?.invite_sent ? 'Applicant portal access sent' : 'Applicant portal link created; email was not sent');
      } else {
        const link = response.data?.portal_url || response.data?.portal_login_url;
        if (!link) throw new Error('No portal link returned');
        await navigator.clipboard.writeText(link);
        toast.success('Secure applicant portal link copied');
      }
      fetchApplicants();
    } catch (error) {
      const gate = extractTenantSetupGate(error);
      if (gate) {
        toast.error(tenantSetupGateToastMessage(gate));
        return;
      }
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : detail?.message || 'Could not create applicant portal access');
    } finally {
      setPortalAccessLoading(null);
    }
  };

  const handleApplicantPortalWhatsAppAccess = async (applicant) => {
    if (!applicant?.id) return;
    const actionKey = `${applicant.id}:whatsapp`;
    setPortalAccessLoading(actionKey);
    try {
      const response = await axios.post(
        `${API}/employees/${applicant.id}/communications/send-whatsapp`,
        {
          template_key: 'onboarding_access',
          request_id: createMessageRequestId(),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const recipient = String(response.data?.message?.to_phone || '').trim();
      toast.success(
        recipient
          ? `Secure CareOps link sent by WhatsApp to +${recipient}`
          : 'Secure CareOps link sent by WhatsApp'
      );
      await fetchApplicants();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : detail?.message || 'Could not send applicant portal access by WhatsApp');
    } finally {
      setPortalAccessLoading(null);
    }
  };

  const handleInviteResultWhatsAppAccess = async () => {
    const applicantId = inviteResult?.applicant_id;
    if (!applicantId || isInviting) return;
    setIsInviting(true);
    try {
      const response = await axios.post(
        `${API}/employees/${applicantId}/communications/send-whatsapp`,
        {
          template_key: 'onboarding_access',
          request_id: createMessageRequestId(),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const recipient = String(response.data?.message?.to_phone || '').trim();
      toast.success(
        recipient
          ? `Applicant invite sent by WhatsApp to +${recipient}`
          : 'Applicant invite sent by WhatsApp'
      );
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : detail?.message || 'Could not send this invite by WhatsApp');
    } finally {
      setIsInviting(false);
    }
  };

  const openInviteResultInWhatsApp = () => {
    const recipient = String(inviteResult?.whatsapp_recipient || '').trim();
    const link = inviteResult?.portal_url || inviteResult?.portal_login_url;
    if (!recipient) {
      toast.error('This applicant does not have a WhatsApp-ready phone number yet');
      return;
    }
    if (!link) {
      toast.error('No secure applicant link is available');
      return;
    }
    const name = String(inviteForm.first_name || '').trim();
    const text = [
      name ? `Hi ${name},` : 'Hello,',
      '',
      'Here is your secure application link:',
      link,
      '',
      'Use this link to continue your application. Reply here if you need a hand.',
    ].join('\n');
    window.open(`https://wa.me/${recipient}?text=${encodeURIComponent(text)}`, '_blank', 'noopener,noreferrer');
  };

  // Handle approval attempt
  const handleApprovalClick = async (applicant) => {
    setSelectedApplicant(applicant);
    setLoadingApprovalCheck(applicant.id);
    
    try {
      // Fetch fresh approval status
      const response = await axios.get(
        `${API}/employees/${applicant.id}/recruitment-approval-check`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const evaluation = response.data;
      setApprovalData(prev => ({ ...prev, [applicant.id]: evaluation }));
      
      if (evaluation.can_approve) {
        // Ready for approval - show confirmation dialog
        setApprovalSetupGate(null);
        setApprovalDialogOpen(true);
      } else {
        // Not ready - show blockers dialog
        setBlockersDialogOpen(true);
      }
    } catch (error) {
      toast.error('Failed to check approval status');
    } finally {
      setLoadingApprovalCheck(null);
    }
  };

  // Execute approval
  const handleApproveRecruitment = async () => {
    if (!selectedApplicant) return;
    
    setIsApproving(true);
    setApprovalSetupGate(null);
    try {
      const response = await axios.post(
        `${API}/employees/${selectedApplicant.id}/approve-recruitment`,
        { notes: approvalNotes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(
        `${selectedApplicant.first_name} ${selectedApplicant.last_name} approved! Employee code: ${response.data.employee_code}`,
        { duration: 5000 }
      );
      
      setApprovalDialogOpen(false);
      setSelectedApplicant(null);
      setApprovalNotes('');
      
      // Redirect to the newly approved employee's profile
      navigate(`/portal/employees/${selectedApplicant.id}?tab=work_readiness`);
      
    } catch (error) {
      const gate = extractTenantSetupGate(error);
      if (gate) {
        setApprovalSetupGate(gate);
        toast.error(tenantSetupGateToastMessage(gate));
        return;
      }
      const detail = error.response?.data?.detail;
      if (typeof detail === 'object' && detail.blockers) {
        setApprovalData(prev => ({
          ...prev,
          [selectedApplicant.id]: {
            ...prev[selectedApplicant.id],
            can_approve: false,
            blockers: detail.blockers
          }
        }));
        setApprovalDialogOpen(false);
        setBlockersDialogOpen(true);
        toast.error('Cannot approve - blockers exist');
      } else {
        toast.error(detail?.message || detail || 'Failed to approve recruitment');
      }
    } finally {
      setIsApproving(false);
    }
  };

  const peopleWorkspaceActions = (
    <>
      {canInviteApplicants && (
        <Button
          onClick={() => {
            resetInviteForm();
            setInviteDialogOpen(true);
          }}
          className="bg-[#0F766E] hover:bg-[#0c5f59] text-white"
          data-testid="invite-applicant-btn"
        >
          <UserPlus className="w-4 h-4 mr-2" />
          Invite Applicant
        </Button>
      )}
      <Button asChild variant="outline">
        <WorkspaceLink to="/portal/inbox?department=recruitment">
          <Inbox className="w-4 h-4 mr-2" />
          Recruitment Inbox
        </WorkspaceLink>
      </Button>
      {canRunIntegrityAudit && (
        <Button
          type="button"
          variant="outline"
          onClick={runPeopleIntegrityAudit}
          disabled={integrityAuditLoading}
          data-testid="people-integrity-audit-btn"
        >
          {integrityAuditLoading ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Shield className="w-4 h-4 mr-2" />
          )}
          Integrity Audit
        </Button>
      )}
    </>
  );

  return (
    <PeopleWorkspaceShell activeView="applicants" actions={peopleWorkspaceActions}>
      <div className="space-y-6 caredock-recruitment-pilot" data-testid="recruitment-page">

      {statusChecksUnavailable && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          Some status checks are temporarily unavailable. Applicant records are still accessible.
        </div>
      )}

      <details className="rounded-xl border border-slate-200 bg-white px-4 py-3" data-testid="recruitment-other-tasks">
        <summary className="cursor-pointer text-sm font-medium text-slate-700">Other tasks</summary>
        <div className="mt-3"><ActionableTaskQueue compact scope="applicant" /></div>
      </details>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-muted w-4 h-4" />
                <Input
                  placeholder="Search applicants..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                  data-testid="recruitment-search"
                />
              </div>
            </div>
            <Select value={statusFilter || 'all'} onValueChange={(v) => setStatusFilter(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-full sm:w-[200px]">
                <SelectValue placeholder="All Stages" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Stages</SelectItem>
                <SelectItem value="new">New</SelectItem>
                <SelectItem value="screening">Screening</SelectItem>
                <SelectItem value="interview">Interview</SelectItem>
                <SelectItem value="compliance_review">Compliance Review</SelectItem>
              </SelectContent>
            </Select>
            <Select value={focusFilter || 'all'} onValueChange={(v) => setFocusFilter(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-full sm:w-[220px]" data-testid="recruitment-focus-filter">
                <SelectValue placeholder="All work" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All work</SelectItem>
                <SelectItem value="access_support">Needs portal access</SelectItem>
                <SelectItem value="applicant_action">Waiting on applicant</SelectItem>
                <SelectItem value="osabea_action">Waiting on your team</SelectItem>
                <SelectItem value="ready_for_approval">Ready for approval</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Applicants List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            Applicants ({visibleApplicants.length})
          </CardTitle>
          <CardDescription>
            Choose an applicant to see their next step. Final approval still requires the necessary checks.
            {focusFilter && <span className="ml-1">Showing {visibleApplicants.length} of {applicants.length} applicants in the selected stage.</span>}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
          ) : applicants.length === 0 ? (
            <div className="text-center py-8 text-text-muted">
              <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No applicants found</p>
            </div>
          ) : visibleApplicants.length === 0 ? (
            <div className="text-center py-8 text-text-muted" data-testid="recruitment-focus-empty">
              <Filter className="w-10 h-10 mx-auto mb-3 opacity-50" />
              <p className="font-medium text-slate-700">No applicants match this focus</p>
              <p className="mt-1 text-sm">Choose another focus or stage to see more applicants.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {visibleApplicants.map((applicant) => {
                const approval = getApprovalSummary(applicant.id);
                const isLoadingApproval = loadingApprovalCheck === applicant.id;
                // Only show status for canonical statuses, fallback UI otherwise
                const showStatus = CANONICAL_STATUSES.includes(normalizeLifecycleStatus(applicant.status));
                const normalizedStage = normalizeLifecycleStatus(applicant.status);
                const focusBadge = recruitmentFocusBadge(applicant, approval);
                const portalAccess = recruitmentPortalAccessState(applicant);
                const nextAction = recruitmentNextActionSummary(applicant, approval);
                const simpleNextAction = plainRecruitmentNextStep({ nextAction, approval });
                const accessRescue = canInviteApplicants
                  ? recruitmentAccessRescue(applicant, nextAction)
                  : null;
                
                return (
                  <div
                    key={applicant.id}
                    className="p-4 rounded-xl border border-border-default hover:border-primary/30 hover:bg-bg-subtle/50 transition-all"
                    data-testid={`applicant-card-${applicant.id}`}
                  >
                    {/* Top Row: Name, Role, Stage */}
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <EmployeeAvatar
                          employeeId={applicant.id}
                          firstName={applicant.first_name}
                          lastName={applicant.last_name}
                          hasPhoto={Boolean(applicant.has_profile_photo)}
                          token={token}
                          size="md"
                        />
                        <div>
                          <p className="font-semibold text-text-primary">
                            {applicant.first_name} {applicant.last_name}
                          </p>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant="outline" className="text-xs">
                              {applicant.role || 'No role assigned'}
                            </Badge>
                            
                          </div>

                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        {focusBadge ? (
                          <Badge
                            variant="outline"
                            className={cn(
                              'text-xs',
                              focusBadge.tone === 'green' && 'bg-emerald-50 text-emerald-700 border-emerald-200',
                              focusBadge.tone === 'amber' && 'bg-amber-50 text-amber-800 border-amber-200',
                              focusBadge.tone === 'blue' && 'bg-blue-50 text-blue-800 border-blue-200',
                              focusBadge.tone === 'slate' && 'bg-slate-50 text-slate-700 border-slate-200',
                            )}
                            data-testid={`recruitment-focus-badge-${applicant.id}`}
                          >
                            {focusBadge.label}
                          </Badge>
                        ) : null}
                      </div>
                    </div>

                    <div
                      className="mb-4 rounded-lg border border-slate-200 bg-white px-3 py-2"
                      data-testid={`recruitment-next-action-${applicant.id}`}
                    >
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">What happens next</p>
                      <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-slate-700">
                        <Badge
                          variant="outline"
                          className={cn(
                            'text-xs',
                            simpleNextAction.owner === 'Applicant'
                              ? 'border-amber-200 bg-amber-50 text-amber-800'
                              : simpleNextAction.owner === 'System'
                                ? 'border-slate-200 bg-slate-50 text-slate-700'
                                : 'border-blue-200 bg-blue-50 text-blue-800',
                          )}
                        >
                          {simpleNextAction.owner}
                        </Badge>
                        <span className="font-medium">{simpleNextAction.label}</span>
                      </div>
                    </div>

                    <details className="mb-4 rounded-lg border border-slate-200 bg-slate-50/60 px-3 py-3" data-testid={`applicant-review-details-${applicant.id}`}>
                      <summary className="cursor-pointer text-sm font-medium text-slate-700">Details and other checks</summary>
                      <div className="mt-3 space-y-3">
                          <div className="mt-2 text-xs text-slate-600" data-testid={`applicant-extra-info-${applicant.id}`}>
                            <p className="mt-2">Stage: {STAGE_LABELS[normalizedStage] || normalizedStage}</p>
                            {applicant.applicant_reference && <p className="mt-2">Reference: {applicant.applicant_reference}</p>}
                          <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-slate-500">
                            <span className="flex items-center gap-1">
                              <Clock className="h-3.5 w-3.5" />
                              Entered {formatDateTime(applicant.stage_entered_at)}
                            </span>
                            <span>Time in stage: {timeInStage(applicant.stage_entered_at)}</span>
                            {applicant.upcoming_interview_at && (
                              <span>Interview: {formatDateTime(applicant.upcoming_interview_at)}</span>
                            )}
                          </div>
                          <div
                            className={cn(
                              'mt-1 text-xs font-medium',
                              portalAccess.tone === 'green' && 'text-emerald-700',
                              portalAccess.tone === 'amber' && 'text-amber-700',
                              portalAccess.tone === 'slate' && 'text-slate-500',
                            )}
                            data-testid={`portal-access-state-${applicant.id}`}
                          >
                            {portalAccess.label}
                          </div>
                          </div>
                    {/* Middle Row: Quick Review Summary with Labeled Progress */}
                    <div className="mb-4 px-3 py-2 bg-gray-50 rounded-lg">
                      {showStatus ? (
                        approval ? (
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4 text-sm">
                              {approval.statusUnavailable ? (
                                <span className="flex items-center gap-1.5 text-slate-600 font-medium">
                                  <AlertTriangle className="w-4 h-4" />
                                  Status unavailable
                                </span>
                              ) : approval.canApprove ? (
                                <span className="flex items-center gap-1.5 text-emerald-600 font-medium">
                                  <CheckCircle className="w-4 h-4" />
                                  Ready for recruitment approval
                                </span>
                              ) : (
                                <span className="flex items-center gap-1.5 text-amber-600 font-medium">
                                  <AlertTriangle className="w-4 h-4" />
                                  Review needed ({approval.blockerCount})
                                </span>
                              )}
                            </div>
                            {!approval.statusUnavailable && (
                              <LabeledProgressBadge
                                metricType="recruitment"
                                completed={approval.verifiedCount}
                                total={approval.requiredCount}
                                size="sm"
                              />
                            )}
                          </div>
                        ) : (
                          <div className="flex items-center gap-2 text-sm text-text-muted">
                            <Loader2 className="w-3 h-3 animate-spin" />
                            Loading approval status...
                          </div>
                        )
                      ) : (
                        <span className="text-xs text-gray-400">Status not required for this stage</span>
                      )}
                    </div>
                    {accessRescue && (
                      <div
                        className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-3"
                        data-testid={`access-rescue-${applicant.id}`}
                      >
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-amber-950">{accessRescue.title}</p>
                            <p className="mt-0.5 text-xs text-amber-900">{accessRescue.description}</p>
                            <p className="mt-1 text-xs font-medium text-amber-800">Send only when you choose to. Copy and Open Chat do not send anything.</p>
                          </div>
                          <div className="flex shrink-0 flex-wrap items-center gap-2">
                            <Button
                              type="button"
                              size="sm"
                              className="bg-primary hover:bg-primary-hover"
                              onClick={() => handleApplicantPortalAccess(applicant, { sendEmail: false })}
                              disabled={portalAccessLoading === `${applicant.id}:copy`}
                              data-testid={`copy-access-link-btn-${applicant.id}`}
                            >
                              {portalAccessLoading === `${applicant.id}:copy` ? (
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              ) : (
                                <Copy className="mr-2 h-4 w-4" />
                              )}
                              {accessRescue.copyLabel}
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              className="border-amber-300 bg-white text-amber-950 hover:bg-amber-100"
                              onClick={() => handleApplicantPortalWhatsAppAccess(applicant)}
                              disabled={portalAccessLoading === `${applicant.id}:whatsapp`}
                              data-testid={`send-access-whatsapp-btn-${applicant.id}`}
                            >
                              {portalAccessLoading === `${applicant.id}:whatsapp` ? (
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              ) : (
                                <MessageCircle className="mr-2 h-4 w-4" />
                              )}
                              Send link by WhatsApp
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              onClick={() => setMessageApplicant(applicant)}
                              data-testid={`access-rescue-message-btn-${applicant.id}`}
                            >
                              Open chat
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}

                    {approval && !approval.canApprove && !approval.statusUnavailable && approval.recruitmentBlockers.length > 0 ? (
                      <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-3" data-testid={`recruitment-blockers-${applicant.id}`}>
                        <p className="text-xs font-semibold uppercase tracking-wide text-amber-900">Blocking applicant approval</p>
                        <ul className="mt-2 space-y-1.5">
                          {approval.recruitmentBlockers.slice(0, 3).map((blocker, index) => (
                            <li key={blocker.requirement_key || blocker.id || blocker.gate || index} className="flex items-start gap-2 text-xs text-amber-950">
                              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                              <span>
                                <strong>{blocker.label || blocker.requirement_key || blocker.id || blocker.gate || 'Requirement'}</strong>
                                {blocker.reason ? ` — ${blocker.reason}` : ''}
                              </span>
                            </li>
                          ))}
                        </ul>
                        {approval.recruitmentBlockers.length > 3 ? (
                          <p className="mt-2 text-xs text-amber-800">+ {approval.recruitmentBlockers.length - 3} more blocker{approval.recruitmentBlockers.length - 3 === 1 ? '' : 's'}</p>
                        ) : null}
                      </div>
                    ) : null}



                      </div>
                    </details>

                    {/* Bottom Row: Actions */}
                    <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                      <div className="flex flex-wrap items-center gap-2">
                        {/* PRIMARY: Review Applicant */}
                        <Button
                          onClick={() => handleReviewApplicant(applicant.id)}
                          className="bg-primary hover:bg-primary-hover"
                          data-testid={`review-applicant-btn-${applicant.id}`}
                        >
                          <Eye className="w-4 h-4 mr-2" />
                          Open applicant
                        </Button>
                      </div>

                      {/* Overflow Menu */}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="outline" size="sm" aria-label={`More actions for ${applicant.first_name || 'applicant'}`} data-testid={`more-applicant-actions-${applicant.id}`}>
                            More <MoreHorizontal className="ml-1.5 w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => setMessageApplicant(applicant)} data-testid={`message-applicant-btn-${applicant.id}`}>
                            <MessageCircle className="w-4 h-4 mr-2" /> Message
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleStageAction(applicant, approval)}
                            disabled={isLoadingApproval}
                            data-testid={`stage-action-btn-${applicant.id}`}
                          >
                            {stageActionLabel(applicant, approval)}
                          </DropdownMenuItem>
                          {!applicant.recruitment_approved && canApprove && approval?.canApprove === true && !approval.statusUnavailable && (
                            <DropdownMenuItem
                              onClick={() => handleApprovalClick(applicant)}
                              disabled={isLoadingApproval}
                              data-testid={`approve-btn-${applicant.id}`}
                            >
                              <CheckCircle className="w-4 h-4 mr-2" /> Approve to onboarding
                            </DropdownMenuItem>
                          )}
                          {!applicant.recruitment_approved && canApprove && approval && !approval.canApprove && (
                            <DropdownMenuItem onClick={() => handleApprovalClick(applicant)} data-testid={`view-blockers-btn-${applicant.id}`}>
                              <AlertTriangle className="w-4 h-4 mr-2" /> See what's missing
                            </DropdownMenuItem>
                          )}
                          {canInviteApplicants && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() => handleApplicantPortalAccess(applicant, { sendEmail: true })}
                                disabled={portalAccessLoading === `${applicant.id}:send`}
                                data-testid={`send-applicant-portal-access-${applicant.id}`}
                              >
                                {portalAccessLoading === `${applicant.id}:send` ? (
                                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                ) : (
                                  <Send className="w-4 h-4 mr-2" />
                                )}
                                Send secure portal link by email
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => handleApplicantPortalAccess(applicant, { sendEmail: false })}
                                disabled={portalAccessLoading === `${applicant.id}:copy`}
                                data-testid={`copy-applicant-portal-link-${applicant.id}`}
                              >
                                {portalAccessLoading === `${applicant.id}:copy` ? (
                                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                ) : (
                                  <Copy className="w-4 h-4 mr-2" />
                                )}
                                Copy secure portal link
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => handleApplicantPortalWhatsAppAccess(applicant)}
                                disabled={portalAccessLoading === `${applicant.id}:whatsapp`}
                                data-testid={`send-applicant-portal-whatsapp-${applicant.id}`}
                              >
                                {portalAccessLoading === `${applicant.id}:whatsapp` ? (
                                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                ) : (
                                  <MessageCircle className="w-4 h-4 mr-2" />
                                )}
                                Send secure portal link by WhatsApp
                              </DropdownMenuItem>
                            </>
                          )}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem 
                            onClick={() => navigate(`/portal/recruitment/${applicant.id}?tab=employment`)}
                          >
                            <Briefcase className="w-4 h-4 mr-2" />
                            1. Recruitment Review
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => navigate(`/portal/recruitment/${applicant.id}?tab=references`)}
                          >
                            <UserCheck className="w-4 h-4 mr-2" />
                            2. References
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => navigate(`/portal/recruitment/${applicant.id}?tab=forms`)}
                          >
                            <FileText className="w-4 h-4 mr-2" />
                            3. Application & Interview
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => navigate(`/portal/recruitment/${applicant.id}?tab=checklist`)}
                          >
                            <CheckCircle className="w-4 h-4 mr-2" />
                            4. Recruitment Checks
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <ProfileMessageDialog
        open={Boolean(messageApplicant)}
        onOpenChange={(open) => {
          if (!open) setMessageApplicant(null);
        }}
        employeeId={messageApplicant?.id || ''}
        employee={messageApplicant}
        profileMode="applicant"
        nextActionTitle={
          messageApplicant?.journey?.next_worker_action?.title
          || messageApplicant?.journey?.next_worker_action?.label
          || messageApplicant?.journey?.next_admin_action?.worker_action?.title
          || ''
        }
      />

      <Dialog open={integrityAuditOpen} onOpenChange={setIntegrityAuditOpen}>
        <DialogContent className="sm:max-w-5xl max-h-[85vh] overflow-y-auto" data-testid="people-integrity-audit-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-[#0F766E]" />
              People Integrity Audit
            </DialogTitle>
            <DialogDescription className="text-left">
              Read-only tenant scan. CareOps does not repair or overwrite any staff/applicant record from this report.
            </DialogDescription>
          </DialogHeader>

          {integrityAudit ? (
            <div className="space-y-4">
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-6">
                {[
                  ['Scanned', integrityAudit.scanned_people ?? 0],
                  ['Applicants', integrityAudit.applicants ?? 0],
                  ['Staff', integrityAudit.staff ?? 0],
                  ['Affected', integrityAudit.affected_people ?? 0],
                  ['Unresolved IDs', integrityAudit.unresolved_link_groups?.length ?? 0],
                  ['Unresolved records', integrityAudit.unresolved_link_records?.length ?? integrityAudit.orphans?.length ?? 0],
                ].map(([label, value]) => (
                  <div key={label} className="rounded-lg border bg-slate-50 p-3">
                    <p className="text-xs text-slate-500">{label}</p>
                    <p className="mt-1 text-xl font-semibold text-slate-900">{value}</p>
                  </div>
                ))}
              </div>

              {integritySourceConsistency && !integritySourceConsistency.sources_consistent ? (
                <div
                  className="rounded-lg border border-red-200 bg-red-50 p-3 text-red-950"
                  data-testid="people-integrity-source-mismatch"
                >
                  <p className="text-sm font-semibold">Audit data source mismatch detected</p>
                  <p className="mt-1 text-xs text-red-900">
                    The read-only audit source disagrees with the operational People records for {integritySourceConsistency.mismatch_count} person{integritySourceConsistency.mismatch_count === 1 ? '' : 's'}. Treat affected audit findings as stale until the data-source configuration is corrected.
                  </p>
                  <div className="mt-3 space-y-2 text-xs">
                    {(integritySourceConsistency.mismatches || []).map((row) => (
                      <div key={row.person_id} className="rounded border border-red-200 bg-white p-2">
                        <p className="font-medium">{row.applicant_reference || row.person_id}</p>
                        <p className="mt-1">
                          {row.diagnostic === 'audit_source_behind_operational'
                            ? 'Operational record is already cleaned and archived, but the audit source still shows the old live employee-code marker.'
                            : 'Operational and audit sources differ and require configuration review.'}
                        </p>
                        <p className="mt-1 text-red-800">
                          Audit code present: {row.audit?.employee_code_present ? 'Yes' : 'No'} ·
                          Operational code present: {row.operational?.employee_code_present ? 'Yes' : 'No'} ·
                          Operational legacy archive: {row.operational?.legacy_pre_activation_archive_present ? 'Present' : 'None'}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              ) : integritySourceConsistency?.sources_consistent ? (
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
                  Audit read source matches the operational People records for the compared lifecycle markers.
                </div>
              ) : null}

              {integritySourceConsistency?.sources_consistent
                && Number(integrityAudit?.anomaly_counts?.employee_code_present_before_activation || 0) === 4
                && (integrityAudit.people || []).filter(
                  (row) => row?.legacy_employee_code_review?.classification === 'safe_legacy_residue_candidate'
                ).length === 4 ? (
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3" data-testid="controlled-legacy-code-cleanup">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm font-semibold text-emerald-950">4 reviewed legacy codes are safe to archive</p>
                      <p className="mt-1 text-xs text-emerald-900">
                        CareOps will re-check lifecycle, approval, activation and staff-use evidence at write time, archive each historical identifier, clear only the live pre-activation code, and verify all four changes.
                      </p>
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      className="bg-emerald-700 hover:bg-emerald-800"
                      onClick={applyControlledLegacyCodeCleanup}
                      disabled={legacyCodeCleanupLoading}
                      data-testid="apply-controlled-legacy-code-cleanup"
                    >
                      {legacyCodeCleanupLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Shield className="mr-2 h-4 w-4" />}
                      Archive 4 safe legacy codes
                    </Button>
                  </div>
                </div>
              ) : null}

              {Object.keys(integrityAudit.anomaly_counts || {}).length > 0 ? (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                  <p className="text-sm font-semibold text-amber-950">Confirmed anomaly summary</p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {Object.entries(integrityAudit.anomaly_counts || {}).map(([code, count]) => (
                      <Badge key={code} variant="outline" className="border-amber-300 bg-white text-amber-900">
                        {code.replace(/_/g, ' ')} · {count}
                      </Badge>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
                  No confirmed integrity anomalies were detected in the scanned tenant records.
                </div>
              )}

              {Object.keys(integrityAudit.observation_counts || {}).length > 0 ? (
                <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
                  <p className="text-sm font-semibold text-blue-950">Observations for review</p>
                  <p className="mt-1 text-xs text-blue-900">
                    These are signals to investigate, not proof that a record is corrupt.
                  </p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {Object.entries(integrityAudit.observation_counts || {}).map(([code, count]) => (
                      <Badge key={code} variant="outline" className="border-blue-300 bg-white text-blue-900">
                        {code.replace(/_/g, ' ')} · {count}
                      </Badge>
                    ))}
                  </div>
                </div>
              ) : null}

              <div className="space-y-2">
                <p className="text-sm font-semibold text-slate-900">Person-by-person result</p>
                {(integrityAudit.people || []).map((row) => (
                  <div key={row.person_id || row.applicant_reference} className="rounded-lg border p-3">
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                      <div>
                        <p className="text-sm font-semibold text-slate-900">
                          {row.applicant_reference || row.employee_code || row.person_id || 'Unknown person'}
                        </p>
                        <p className="text-xs text-slate-500">
                          {row.kind} · {row.lifecycle_status || 'unknown'}
                          {row.person_id ? ` · ${row.person_id}` : ''}
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {row.applicant_progress ? (
                          <Badge variant="outline">
                            Applicant {row.applicant_progress.percentage}% ({row.applicant_progress.completed_steps}/{row.applicant_progress.total_steps})
                          </Badge>
                        ) : null}
                        {row.kind === 'staff' && row.staff_file_readiness?.available ? (
                          <Badge variant="outline">
                            Staff file {row.staff_file_readiness.percentage}% ({row.staff_file_readiness.completed_requirements}/{row.staff_file_readiness.total_requirements})
                          </Badge>
                        ) : null}
                        {row.kind === 'staff' && row.staff_file_readiness && !row.staff_file_readiness.available ? (
                          <Badge variant="outline" className="text-slate-500">
                            Staff file N/A
                          </Badge>
                        ) : null}
                      </div>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {(row.anomalies || []).length > 0 ? (row.anomalies || []).map((code) => (
                        <Badge key={code} className="bg-amber-100 text-amber-900 hover:bg-amber-100">
                          {code.replace(/_/g, ' ')}
                        </Badge>
                      )) : (
                        <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">No confirmed anomaly</Badge>
                      )}
                      {(row.observations || []).map((code) => (
                        <Badge key={'obs-' + code} className="bg-blue-100 text-blue-900 hover:bg-blue-100">
                          Observation: {code.replace(/_/g, ' ')}
                        </Badge>
                      ))}
                    </div>
                    {row.legacy_employee_code_review ? (
                      <div
                        className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-950"
                        data-testid={`legacy-code-review-${row.person_id}`}
                      >
                        <p className="text-sm font-semibold">Legacy employee code review</p>
                        {row.legacy_employee_code_review.classification === 'safe_legacy_residue_candidate' ? (
                          <>
                            <p className="mt-1">
                              No recruitment approval, approval timestamp, activation timestamp or worker invite evidence is recorded.
                            </p>
                            <p className="mt-1 font-medium text-emerald-800">Safe legacy residue candidate for controlled cleanup.</p>
                          </>
                        ) : row.legacy_employee_code_review.classification === 'retain_historical_code' ? (
                          <>
                            <p className="mt-1">
                              CareOps found documented recruitment-approval revocation history.
                            </p>
                            <p className="mt-1 font-medium">Retain the historical employee code; do not clear it automatically.</p>
                          </>
                        ) : (
                          <>
                            <p className="mt-1">
                              CareOps found activation/approval evidence while the person is currently back in an applicant lifecycle.
                            </p>
                            <p className="mt-1 font-medium">Manual lifecycle review required before changing the code.</p>
                          </>
                        )}
                        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-amber-800">
                          <span>Approved now: {row.legacy_employee_code_review.recruitment_approved ? 'Yes' : 'No'}</span>
                          <span>Approval timestamp: {row.legacy_employee_code_review.approval_at_present ? 'Present' : 'None'}</span>
                          <span>Revocation timestamp: {row.legacy_employee_code_review.approval_revoked_at_present ? 'Present' : 'None'}</span>
                          <span>Activation timestamp: {row.legacy_employee_code_review.activated_at_present ? 'Present' : 'None'}</span>
                          <span>Worker invite: {row.legacy_employee_code_review.worker_invite_at_present ? 'Present' : 'None'}</span>
                        </div>
                      </div>
                    ) : null}
                    {(row.anomalies || []).includes('application_profile_phone_conflict') ? (
                      <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3" data-testid={`phone-conflict-review-${row.person_id}`}>
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <div>
                            <p className="text-sm font-semibold text-amber-950">Phone conflict requires manual review</p>
                            <p className="mt-1 text-xs text-amber-900">
                              CareOps will not choose or overwrite either number automatically.
                            </p>
                          </div>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="border-amber-300 bg-white text-amber-900"
                            onClick={() => reviewPhoneConflict(row.person_id)}
                            disabled={contactConflictLoading === row.person_id}
                            data-testid={`review-phone-conflict-btn-${row.person_id}`}
                          >
                            {contactConflictLoading === row.person_id ? (
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            ) : (
                              <Eye className="mr-2 h-4 w-4" />
                            )}
                            Review provenance
                          </Button>
                        </div>
                        {contactConflictReviews[row.person_id] ? (
                          <div className="mt-3 grid gap-2 text-xs text-amber-950 sm:grid-cols-2">
                            <div className="rounded border border-amber-200 bg-white p-2">
                              <p className="font-medium">Profile source</p>
                              <p>Record updated: {formatDateTime(contactConflictReviews[row.person_id]?.profile_source?.record_updated_at)}</p>
                              <p>Phone-specific provenance: {formatDateTime(contactConflictReviews[row.person_id]?.profile_source?.phone_provenance_at)}</p>
                            </div>
                            <div className="rounded border border-amber-200 bg-white p-2">
                              <p className="font-medium">Submitted application source</p>
                              <p>Status: {contactConflictReviews[row.person_id]?.application_source?.status || 'Not recorded'}</p>
                              <p>Submitted: {formatDateTime(contactConflictReviews[row.person_id]?.application_source?.submitted_at)}</p>
                            </div>
                            <div className="sm:col-span-2 rounded border border-amber-300 bg-white p-2">
                              <p className="font-medium">
                                {contactConflictReviews[row.person_id]?.recommended_next_step === 'manual_confirmation_required'
                                  ? 'Manual confirmation required'
                                  : contactConflictReviews[row.person_id]?.recommended_next_step === 'review_phone_specific_provenance'
                                    ? 'Phone-specific provenance is available for review'
                                    : 'No unresolved phone conflict'}
                              </p>
                              <p className="mt-1 text-amber-800">
                                No phone digits or other raw personal contents are exposed by this integrity review.
                              </p>
                            </div>
                            <div className="sm:col-span-2 rounded border border-slate-200 bg-white p-3">
                              <p className="font-medium text-slate-950">Resolve only after independent confirmation</p>
                              <p className="mt-1 text-slate-600">
                                Confirm the current number through a trusted/authenticated channel. CareOps preserves the submitted application as historical evidence.
                              </p>
                              <label className="mt-3 flex items-start gap-2 text-slate-800">
                                <input
                                  type="checkbox"
                                  className="mt-0.5 h-4 w-4"
                                  checked={Boolean(contactConflictConfirmed[row.person_id])}
                                  onChange={(event) => setContactConflictConfirmed((prev) => ({
                                    ...prev,
                                    [row.person_id]: event.target.checked,
                                  }))}
                                  data-testid={`phone-conflict-confirmed-${row.person_id}`}
                                />
                                <span>I independently confirmed which number is current.</span>
                              </label>
                              <div className="mt-3 flex flex-wrap gap-2">
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  disabled={!contactConflictConfirmed[row.person_id] || Boolean(contactConflictResolveLoading)}
                                  onClick={() => resolvePhoneConflict(row.person_id, 'profile_confirmed_current')}
                                  data-testid={`confirm-profile-phone-${row.person_id}`}
                                >
                                  {contactConflictResolveLoading === `${row.person_id}:profile_confirmed_current`
                                    ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    : <Shield className="mr-2 h-4 w-4" />}
                                  Profile number is current
                                </Button>
                                <Button
                                  type="button"
                                  size="sm"
                                  className="bg-[#0F766E] hover:bg-[#0c5f59]"
                                  disabled={!contactConflictConfirmed[row.person_id] || Boolean(contactConflictResolveLoading)}
                                  onClick={() => resolvePhoneConflict(row.person_id, 'application_promoted_to_profile')}
                                  data-testid={`use-application-phone-${row.person_id}`}
                                >
                                  {contactConflictResolveLoading === `${row.person_id}:application_promoted_to_profile`
                                    ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    : <CheckCircle className="mr-2 h-4 w-4" />}
                                  Use application number as current
                                </Button>
                              </div>
                            </div>
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                    {(row.anomalies || []).includes('onboarding_status_lifecycle_mismatch') ? (
                      <div className="mt-2 space-y-1 text-xs text-amber-800">
                        <p>
                          Stored onboarding status: {row.stored_onboarding_status || 'blank'} · Expected from lifecycle: {row.expected_onboarding_status || 'unknown'}
                        </p>
                        {row.lifecycle_reconciliation?.type === 'sync_onboarding_status_to_lifecycle' ? (
                          <p>
                            Suggested safe repair: keep lifecycle <strong>{row.lifecycle_status}</strong> and sync the compatibility onboarding status to <strong>{row.lifecycle_reconciliation.proposed_onboarding_status}</strong>.
                          </p>
                        ) : null}
                        {row.lifecycle_reconciliation?.type === 'advance_stage_from_submitted_application' ? (
                          <p>
                            Read-only stage proposal: submitted application evidence supports moving lifecycle from <strong>{row.lifecycle_status}</strong> to <strong>{row.lifecycle_reconciliation.proposed_lifecycle_status}</strong>, which maps onboarding status to <strong>{row.lifecycle_reconciliation.proposed_onboarding_status}</strong>.
                          </p>
                        ) : null}
                        {row.lifecycle_reconciliation?.type === 'review_required' ? (
                          <p>Manual review required before changing either lifecycle or onboarding status.</p>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>

              {(integrityAudit.legacy_linked_records || []).length > 0 ? (
                <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-blue-950">
                  <p className="text-sm font-semibold">Retained legacy-linked records</p>
                  <p className="mt-1 text-xs">
                    {integrityAudit.legacy_linked_records.length} record{integrityAudit.legacy_linked_records.length === 1 ? '' : 's'} use a trusted historical person ID that maps to a current CareOps person.
                  </p>
                </div>
              ) : null}

              {(integrityAudit.unresolved_link_groups || []).length > 0 ? (
                <div className="rounded-lg border border-slate-300 bg-slate-50 p-3">
                  <p className="text-sm font-semibold text-slate-950">Unresolved historical ownership links</p>
                  <p className="mt-1 text-xs text-slate-700">
                    These records point to person IDs that are no longer present on the current People table and have no stored alias mapping. They are not automatically corrupt or safe to delete.
                  </p>
                  <div className="mt-3 space-y-2 text-xs text-slate-800">
                    {(integrityAudit.unresolved_link_groups || []).map((group) => (
                      <div key={group.linked_person_id} className="rounded border bg-white p-2">
                        <p className="font-medium">{group.linked_person_id}</p>
                        <p>
                          {group.record_count} linked record{group.record_count === 1 ? '' : 's'}
                          {group.collections?.length ? ' · ' + group.collections.join(', ') : ''}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

              <p className="text-xs text-slate-500">{integrityAudit.privacy_note}</p>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
        <DialogContent className="sm:max-w-2xl" data-testid="private-applicant-invite-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-[#0F766E]" />
              Invite Applicant
            </DialogTitle>
            <DialogDescription className="text-left">
              Send one private applicant link for this workspace. The applicant will enter Recruitment first, not Employees.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="rounded-lg border border-teal-100 bg-teal-50 px-4 py-3 text-sm text-teal-900">
              This is the tenant-safe route for applications. Public applications stay closed; each invite belongs to this workspace.
            </div>

            {inviteSetupGate && (
              <TenantSetupGateNotice
                gate={inviteSetupGate}
                title="Workspace setup must be complete before applicants are invited"
                compact
              />
            )}

            <div className="grid gap-3 md:grid-cols-2">
              <div className="space-y-1.5">
                <label className="text-sm font-medium">First name *</label>
                <Input
                  value={inviteForm.first_name}
                  onChange={(e) => setInviteForm((prev) => ({ ...prev, first_name: e.target.value }))}
                  placeholder="e.g. Jane"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Last name *</label>
                <Input
                  value={inviteForm.last_name}
                  onChange={(e) => setInviteForm((prev) => ({ ...prev, last_name: e.target.value }))}
                  placeholder="e.g. Smith"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Email *</label>
                <Input
                  type="email"
                  value={inviteForm.email}
                  onChange={(e) => setInviteForm((prev) => ({ ...prev, email: e.target.value }))}
                  placeholder="name@example.com"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Mobile / WhatsApp number</label>
                <Input
                  type="tel"
                  value={inviteForm.phone}
                  onChange={(e) => setInviteForm((prev) => ({ ...prev, phone: e.target.value }))}
                  placeholder="e.g. 07700 900123 or +44 7700 900123"
                />
                <p className="text-xs text-slate-500">
                  Optional for the invite email. UK 07 numbers are accepted; for non-UK numbers include the country code.
                </p>
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Role applied for *</label>
                <Select value={inviteForm.role} onValueChange={(role) => setInviteForm((prev) => ({ ...prev, role }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Healthcare Assistant">Healthcare Assistant</SelectItem>
                    <SelectItem value="Nurse">Nurse</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Internal note</label>
                <Textarea
                  value={inviteForm.notes}
                  onChange={(e) => setInviteForm((prev) => ({ ...prev, notes: e.target.value }))}
                  placeholder="Optional note for the recruitment team"
                  rows={2}
                />
              </div>
            </div>

            {inviteResult?.portal_url && (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
                <p className="text-sm font-semibold text-emerald-900">
                  {inviteResult.invite_sent ? 'Invite sent' : 'Private link created'}
                </p>
                <p className="mt-1 text-sm text-emerald-800">
                  Reference: {inviteResult.applicant_reference}. The applicant now appears in Recruitment for this tenant only.
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button variant="outline" className="border-emerald-300 text-emerald-800" onClick={copyInviteLink}>
                    <Copy className="w-4 h-4 mr-2" />
                    Copy private link
                  </Button>
                  <Button
                    type="button"
                    className="bg-emerald-700 hover:bg-emerald-800"
                    onClick={handleInviteResultWhatsAppAccess}
                    disabled={isInviting || !inviteResult.whatsapp_recipient}
                    data-testid="send-new-applicant-whatsapp-invite"
                  >
                    {isInviting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <MessageCircle className="w-4 h-4 mr-2" />}
                    Send invite by WhatsApp
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    className="border-emerald-300 text-emerald-800"
                    onClick={openInviteResultInWhatsApp}
                    disabled={!inviteResult.whatsapp_recipient}
                    data-testid="open-new-applicant-whatsapp"
                  >
                    <MessageCircle className="w-4 h-4 mr-2" />
                    Open WhatsApp
                  </Button>
                </div>
                {!inviteResult.whatsapp_recipient ? (
                  <p className="mt-2 text-xs text-amber-800">
                    Add a valid mobile / WhatsApp number before sending this invite by WhatsApp.
                  </p>
                ) : null}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setInviteDialogOpen(false)}>
              Close
            </Button>
            <Button
              onClick={handleCreateInvite}
              disabled={isInviting || !inviteForm.first_name || !inviteForm.last_name || !inviteForm.email}
              className="bg-[#0F766E] hover:bg-[#0c5f59]"
              data-testid="send-private-applicant-invite-btn"
            >
              {isInviting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Send Private Invite
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approval Confirmation Dialog */}
      <Dialog open={approvalDialogOpen} onOpenChange={setApprovalDialogOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="approval-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCheck className="h-5 w-5 text-emerald-600" />
              Approve to Staff Onboarding?
            </DialogTitle>
            <DialogDescription className="text-left">
              This will transition <strong>{selectedApplicant?.first_name} {selectedApplicant?.last_name}</strong> from 
              Applicant to Employee status after recruitment review is complete.
            </DialogDescription>
          </DialogHeader>
          
          {selectedApplicant && (
            <div className="py-4">
              <div className="flex items-center gap-3 mb-4 p-3 bg-gray-50 rounded-lg">
                <EmployeeAvatar
                  firstName={selectedApplicant.first_name}
                  lastName={selectedApplicant.last_name}
                  size="md"
                />
                <div>
                  <p className="font-medium">
                    {selectedApplicant.first_name} {selectedApplicant.last_name}
                  </p>
                  <p className="text-sm text-text-muted">{selectedApplicant.role}</p>
                </div>
              </div>
              
              <div className="text-sm text-gray-600 mb-4 p-3 bg-emerald-50 rounded-lg border border-emerald-100">
                <p className="font-medium text-emerald-800 mb-2">This action will:</p>
                <ul className="list-disc ml-4 space-y-1 text-emerald-700">
                  <li>Assign an official employee code</li>
                  <li>Move from <strong>Applicant</strong> to <strong>Employee</strong></li>
                  <li>Set status to <strong>Onboarding</strong></li>
                  <li>Record that each recruitment review tab was signed off</li>
                  <li>Enable employee actions and scheduling</li>
                </ul>
              </div>

              {approvalSetupGate && (
                <div className="mb-4">
                  <TenantSetupGateNotice
                    gate={approvalSetupGate}
                    title="Workspace setup must be complete before onboarding starts"
                    compact
                  />
                </div>
              )}
              
              <div className="space-y-2">
                <label className="text-sm font-medium">Approval Notes (optional)</label>
                <Textarea
                  placeholder="Add any final notes from employment, interview, or recruitment review..."
                  value={approvalNotes}
                  onChange={(e) => setApprovalNotes(e.target.value)}
                  rows={3}
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setApprovalDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleApproveRecruitment} 
              disabled={isApproving}
              className="bg-[#0F5C5E] hover:bg-[#0c4a4c]"
              data-testid="confirm-approve-recruitment-btn"
            >
              {isApproving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle className="w-4 h-4 mr-2" />
              )}
              Approve to Onboarding
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Blockers Dialog */}
      <Dialog open={blockersDialogOpen} onOpenChange={setBlockersDialogOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="blockers-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              <AlertTriangle className="h-5 w-5" />
              Cannot Approve Yet
            </DialogTitle>
            <DialogDescription>
              Resolve the items below. This now includes the final sign-off required on each recruitment review tab.
            </DialogDescription>
          </DialogHeader>
          
          {selectedApplicant && approvalData[selectedApplicant.id] && (
            <div className="py-4">
              <div className="flex items-center gap-3 mb-4 p-3 bg-gray-50 rounded-lg">
                <EmployeeAvatar
                  firstName={selectedApplicant.first_name}
                  lastName={selectedApplicant.last_name}
                  size="sm"
                />
                <div>
                  <p className="font-medium text-sm">
                    {selectedApplicant.first_name} {selectedApplicant.last_name}
                  </p>
                  <p className="text-xs text-text-muted">
                    {approvalData[selectedApplicant.id].verified_count} / {approvalData[selectedApplicant.id].required_count} verified
                  </p>
                </div>
              </div>
              
              <div className="max-h-64 overflow-y-auto space-y-2">
                {approvalData[selectedApplicant.id].blockers?.map((blocker, idx) => (
                  <div 
                    key={blocker.requirement_key || idx}
                    className="flex items-start gap-3 p-3 bg-red-50 rounded-lg border border-red-100"
                  >
                    <div className="w-6 h-6 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <XCircle className="w-3.5 h-3.5 text-red-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-800 text-sm">{blocker.label}</p>
                      <p className="text-xs text-red-600">{blocker.reason}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setBlockersDialogOpen(false)}>
              Close
            </Button>
            <Button 
              onClick={() => {
                setBlockersDialogOpen(false);
                if (selectedApplicant) {
                  navigate(`/portal/recruitment/${selectedApplicant.id}?tab=checklist`);
                }
              }}
              className="bg-primary hover:bg-primary-hover"
            >
              <Eye className="w-4 h-4 mr-2" />
              Review Applicant File
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </PeopleWorkspaceShell>
  );
}
